import { useState, Fragment } from "react";
import { formatTimestamp } from "@/lib/date-utils";
import { trpc } from "@/trpc";
import { Button } from "@satyrsmc/shared/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import type { AdminBikeSuggestion } from "@satyrsmc/shared/dto/admin/bikeSuggestion";
import { BikeSuggestionReviewAction, BikeSuggestionType } from "@satyrsmc/shared/lib/enums";

type ExpandedState = {
  id: string;
  action: BikeSuggestionReviewAction;
  make: string;
  model: string;
  rejectNote: string;
};

export function AdminGaragePanel() {
  const utils = trpc.useUtils();
  const { data, isLoading } = trpc.admin.bikeSuggestions.listPending.useQuery();
  const [expanded, setExpanded] = useState<ExpandedState | null>(null);

  const reviewMutation = trpc.admin.bikeSuggestions.review.useMutation({
    onSuccess: () => {
      utils.admin.bikeSuggestions.listPending.invalidate();
      utils.admin.bikeSuggestions.pendingCount.invalidate();
      utils.members.bikeSuggestions.listApproved.invalidate();
      setExpanded(null);
    },
  });

  function openExpand(item: AdminBikeSuggestion, action: BikeSuggestionReviewAction) {
    if (expanded?.id === item.id && expanded.action === action) {
      setExpanded(null);
      return;
    }
    setExpanded({
      id: item.id,
      action,
      make: item.make,
      model: item.model ?? "",
      rejectNote: "",
    });
  }

  function handleConfirm() {
    if (!expanded) return;
    reviewMutation.mutate({
      id: expanded.id,
      action: expanded.action,
      make: expanded.make || undefined,
      model: expanded.model || undefined,
      admin_note: expanded.rejectNote || undefined,
    });
  }

  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Garage Suggestions</h1>
        <p className="mt-1 text-muted-foreground">
          Review member-submitted motorcycle makes and models.
        </p>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">No pending suggestions.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Type</TableHead>
              <TableHead>Make</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Submitted by</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <Fragment key={item.id}>
                <TableRow>
                  <TableCell>
                    <Badge variant="outline">
                      {item.type === BikeSuggestionType.Make ? "Make" : "Model"}
                    </Badge>
                  </TableCell>
                  <TableCell>{item.make}</TableCell>
                  <TableCell>{item.model ?? "—"}</TableCell>
                  <TableCell>{item.user_name ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatTimestamp(item.created_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        disabled={reviewMutation.isPending}
                        onClick={() => openExpand(item, BikeSuggestionReviewAction.Approve)}
                      >
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={reviewMutation.isPending}
                        onClick={() => openExpand(item, BikeSuggestionReviewAction.Reject)}
                      >
                        Reject
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
                {expanded?.id === item.id && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-muted/50 pb-4 pt-2">
                      <div className="space-y-3 px-2">
                        {expanded.action === BikeSuggestionReviewAction.Approve && (
                          <div className="grid gap-3 sm:grid-cols-2">
                            <div className="space-y-1">
                              <Label htmlFor={`edit-make-${item.id}`}>Make</Label>
                              <Input
                                id={`edit-make-${item.id}`}
                                value={expanded.make}
                                onChange={(e) =>
                                  setExpanded((s) => s && { ...s, make: e.target.value })
                                }
                              />
                            </div>
                            {item.type === BikeSuggestionType.Model && (
                              <div className="space-y-1">
                                <Label htmlFor={`edit-model-${item.id}`}>Model</Label>
                                <Input
                                  id={`edit-model-${item.id}`}
                                  value={expanded.model}
                                  onChange={(e) =>
                                    setExpanded((s) => s && { ...s, model: e.target.value })
                                  }
                                />
                              </div>
                            )}
                          </div>
                        )}
                        {expanded.action === BikeSuggestionReviewAction.Reject && (
                          <div className="space-y-1">
                            <Label htmlFor={`reject-note-${item.id}`}>
                              Rejection note (optional)
                            </Label>
                            <Textarea
                              id={`reject-note-${item.id}`}
                              value={expanded.rejectNote}
                              onChange={(e) =>
                                setExpanded((s) => s && { ...s, rejectNote: e.target.value })
                              }
                              placeholder="Explain why this suggestion is being rejected..."
                              rows={2}
                            />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant={
                              expanded.action === BikeSuggestionReviewAction.Reject
                                ? "destructive"
                                : "default"
                            }
                            disabled={
                              reviewMutation.isPending ||
                              (expanded.action === BikeSuggestionReviewAction.Approve &&
                                !expanded.make.trim())
                            }
                            onClick={handleConfirm}
                          >
                            {expanded.action === BikeSuggestionReviewAction.Approve
                              ? "Confirm Approve"
                              : "Confirm Reject"}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setExpanded(null)}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
